import React, { useEffect, useState } from 'react';
import { PropTypes } from 'prop-types';
import { Button, Drawer, Field, Input } from '@itds/elements/index.js';

import { Api } from '@brokerage/utils/Api';

const New = ({ product, onClose, onSave, show }) => {
  const [input_fields, setInputFields] = useState({});
  const [features, setFeatures] = useState([]);
  const [resources, setResources] = useState([]);
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    const new_product = Object.assign({}, product);

    setInputFields(new_product);
    setFeatures(new_product.features || []);
    setResources(new_product.resources || []);
  }, [product]);

  const handleInputChange = (name, e) => {
    setInputFields(Object.assign({}, input_fields, { [name]: e.target.value }));
  };

  const handleSave = e => {
    const errors = [];
    const regex = new RegExp('^(http|https)://[a-zA-Z0-9]', 'i');

    setErrors(errors);

    e.preventDefault();

    if (!input_fields.name) {
      errors.push('name');
    }

    ['label', 'url', 'order'].forEach(field_name => {
      resources.forEach(resource => {
        if (!resource[field_name]) {
          errors.push(`resource-${resource.id}-${field_name}`);
        }

        if (field_name === 'url' && !regex.test(resource[field_name])) {
          errors.push(`resource-${resource.id}-url`);
        }
      });
    });

    ['label', 'order'].forEach(field_name => {
      features.forEach(feature => {
        if (!feature[field_name]) {
          errors.push(`feature-${feature.id}-${field_name}`);
        }
      });
    });

    if (errors.length) {
      setErrors(errors);
    } else {
      Api.saveCarrierProduct(product ? product.id : null, {
        ...input_fields,
        features,
        resources
      }).then(() => {
        onSave();
      });
    }
  };

  const handleAddFeature = () => {
    const feature = { id: features.length + 1, label: null, order: null };

    setFeatures([...features, feature]);
  };

  const handleAddResource = () => {
    const resource = { id: resources.length + 1, label: null, url: null, order: null };

    setResources([...resources, resource]);
  };

  const handleResourceChange = (resource, name, e) => {
    const new_resources = resources.map(r => {
      if (r.id === resource.id) {
        return Object.assign({}, r, { [name]: e.target.value });
      }

      return r;
    });

    setResources(new_resources);
  };

  const handleFeatureChange = (feature, name, e) => {
    const new_features = features.map(f => {
      if (f.id === feature.id) {
        return Object.assign({}, f, { [name]: e.target.value });
      }

      return f;
    });

    setFeatures(new_features);
  };

  const handleResourceRemove = resource => {
    const new_resources = resources.filter(r => {
      return r.id !== resource.id;
    });

    setResources(new_resources);
  };

  const handleFeatureRemove = feature => {
    const new_features = features.filter(r => {
      return r.id !== feature.id;
    });

    setFeatures(new_features);
  };

  return (
    <Drawer
      alignRight={true}
      footer={
        <Button onClick={handleSave}>{product ? 'Save Changes' : 'Create New Product'}</Button>
      }
      onClose={onClose}
      show={show}
      title={product ? `Editing: ${product.name}` : 'New Product'}
    >
      <Field label='Name'>
        <Input
          invalid={errors.includes('name')}
          onChange={handleInputChange.bind(null, 'name')}
          value={input_fields.name}
        />
      </Field>
      <Field label='Summary'>
        <Input
          height={150}
          onChange={handleInputChange.bind(null, 'summary')}
          type='textarea'
          value={input_fields.summary}
        />
      </Field>
      <Field label='LifeDX ID'>
        <Input
          onChange={handleInputChange.bind(null, 'lifedx_id')}
          value={input_fields.lifedx_id}
        />
      </Field>
      <Field label='FireLight Cusip'>
        <Input
          onChange={handleInputChange.bind(null, 'firelight_cusip')}
          value={input_fields.firelight_cusip}
        />
      </Field>

      {/* Features */}
      <Field label='Features'>
        {features.map((feature, i) => {
          return (
            <div key={i} style={{ display: 'flex', marginBottom: 15 }}>
              <Input
                invalid={errors.includes(`feature-${feature.id}-label`)}
                onChange={handleFeatureChange.bind(null, feature, 'label')}
                placeholder='Label'
                required={true}
                style={{ flex: 1, marginRight: 10 }}
                value={feature.label}
              />
              <Input
                invalid={errors.includes(`feature-${feature.id}-order`)}
                min={1}
                onChange={handleFeatureChange.bind(null, feature, 'order')}
                placeholder='Order'
                required={true}
                style={{ width: 70, marginRight: 10 }}
                type='number'
                value={feature.order}
              />
              <Button color='RED' icon='delete' onClick={handleFeatureRemove.bind(null, feature)} />
            </div>
          );
        })}

        <Button inline={true} onClick={handleAddFeature} small={true}>
          Add Feature
        </Button>
      </Field>

      {/* Resources */}
      <Field label='Resources'>
        {resources.map((resource, i) => {
          return (
            <div key={i} style={{ display: 'flex', marginBottom: 15 }}>
              <Input
                invalid={errors.includes(`resource-${resource.id}-label`)}
                onChange={handleResourceChange.bind(null, resource, 'label')}
                placeholder='Label'
                required={true}
                style={{ flex: 1, marginRight: 10 }}
                value={resource.label}
              />
              <Input
                invalid={errors.includes(`resource-${resource.id}-url`)}
                onChange={handleResourceChange.bind(null, resource, 'url')}
                placeholder='URL'
                required={true}
                style={{ flex: 1, marginRight: 10 }}
                value={resource.url}
              />
              <Input
                invalid={errors.includes(`resource-${resource.id}-order`)}
                min={1}
                onChange={handleResourceChange.bind(null, resource, 'order')}
                placeholder='Order'
                required={true}
                style={{ width: 70, marginRight: 10 }}
                type='number'
                value={resource.order}
              />
              <Button
                color='RED'
                icon='delete'
                onClick={handleResourceRemove.bind(null, resource)}
              />
            </div>
          );
        })}

        <Button inline={true} onClick={handleAddResource} small={true}>
          Add Resource
        </Button>
      </Field>
    </Drawer>
  );
};

New.propTypes = {
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  product: PropTypes.object,
  show: PropTypes.bool
};

export default New;
